import { useEffect, useLayoutEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name of the dialog. */
  label: string;
  /** Class for the fixed backdrop element (e.g. "modal-overlay"). */
  overlayClassName: string;
  /** Class for the dialog panel (e.g. "workout-modal-inner"). */
  panelClassName: string;
  /** Optional extra wrapper around the overlay (v5 modals need `.ezf-v5`). */
  wrapClassName?: string;
  /**
   * Close when the backdrop is clicked. Pass `false` while a form is dirty so a
   * stray click can't wipe the user's input (Esc still closes — that's deliberate).
   */
  closeOnOverlay?: boolean;
  panelStyle?: CSSProperties;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared dialog behavior for every modal: role/aria wiring, Esc to close,
 * backdrop click (optional), body scroll lock, focus trap and focus restore.
 * Purely behavioral — visual styling stays with the caller's own classes.
 */
export function ModalShell({
  open,
  onClose,
  label,
  overlayClassName,
  panelClassName,
  wrapClassName,
  closeOnOverlay = true,
  panelStyle,
  children,
}: ModalShellProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Capture the trigger and move focus into the dialog before paint.
  useLayoutEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const target = panel.querySelector<HTMLElement>('[data-autofocus]') ?? panel;
      target.focus({ preventScroll: true });
    }
    return () => {
      restoreRef.current?.focus?.({ preventScroll: true });
      restoreRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (nodes.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const overlay = (
    <div
      className={overlayClassName}
      onMouseDown={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={panelClassName}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );

  return wrapClassName ? <div className={wrapClassName}>{overlay}</div> : overlay;
}
